import { useAuth, getAccessToken, setAccessToken } from "/@/Auth";
import { resetScrollsOnCalculate } from "/@/app/scrolls";
import { CustomError, showGlobalError} from "/@/app/utils";

export function fetchJWT(url, options = {})
{

	options.headers = options.headers || {};
	const access_token=getAccessToken();
	options.headers['Authorization'] = `Bearer ${access_token}`;

	return fetch(url, options).then(response =>
	{
		if (response.status === 401)
		{
			const refresh_token=localStorage.getItem('jwt_refresh')

			if (!refresh_token) return response;

			// 401 = access token expired, try refresh
			return fetch(window.appConfig.backend_url+'/api/token/refresh/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ refresh: localStorage.getItem('jwt_refresh') })
			})
			.then(refreshRes => {
				if (!refreshRes.ok) return refreshRes;
				return refreshRes.json();
			})
			.then(tokens => {
				if ('status' in tokens) return tokens;
				// store new access token
				setAccessToken(tokens.access);

				options.headers['Authorization'] = `Bearer ${tokens.access}`;
				return fetch(url, options);
			});
		};
		return response; // original request succeeded
	});
}



export function useApi()
{
const auth=useAuth();
const { handleAuthFailure, isAuthenticated } = auth ? auth : {handleAuthFailure:()=>{},isAuthenticated:false};

function request(fu, params = {}, opts={})
{
	if (!isAuthenticated)
	{
		handleAuthFailure({ text: 'Требуется вход', type: "info" });
		if (opts.supress_nonauth) return Promise.resolve({ skipped: true, reason: 'nonauth_supressed' });
		return Promise.reject(new Error("Not authenticated"));
	};
	if (opts.disable)
	{
		if (opts.disable.dataset.loading) return Promise.resolve({ skipped: true, reason: 'already_running' });
		opts.disable.dataset.loading='1';
		opts.disable.disabled=true;
	};

	let silentStatuses=opts.silentStatuses;
	if (silentStatuses === undefined)  silentStatuses = {};
	if (silentStatuses === "__all__") silentStatuses = { 400: true, 401: true, 403: true, 404: true, 409: true, 422: true, 500: true, 501: true, 502: true, 504: true, };

	let url=fu.includes('://')?fu:(opts.api_url?(opts.api_url.includes('://')?opts.api_url:(window.appConfig.backend_url+(opts.api_url.startsWith('/')?opts.api_url:'/api/'+opts.api_url))):window.appConfig.backend_url+(fu.startsWith('/')?'':'/api/v1'))+(fu.startsWith('/')?fu:'/'+fu);
	let options={};
	options.method=opts.method?.toUpperCase() || 'GET';
	options.headers=opts.headers || {};
	if (['PUT', 'POST', 'DELETE', 'PATCH'].includes(options.method))
	{
		options.headers['Content-Type'] = 'application/json';
	};
	const fetch_func=opts['auth']===false ? fetch : fetchJWT;

	if (Object.keys(params).length) {
		if (['PUT', 'POST', 'PATCH'].includes(options.method)) options.body=JSON.stringify(params);
		else if (options.method=='GET') url+='?'+(new URLSearchParams(params).toString());
	};

	// for streaming abort
	let signal=opts.signal;
	let options_copy=structuredClone(options);
	if (signal) options_copy.signal=signal;

	return fetch_func(url, options_copy)
        .then(async response =>
	{
		if (opts.disable) { delete opts.disable.dataset.loading; opts.disable.disabled=false;};
		let statusCode = response.status;
		if (opts.raw) return response;
		if (opts.streaming) return response;

		let isJson = (response.headers.get('Content-Type')||'').includes('application/json');
		let data = isJson ? await response.json() : await response.text();

		if (statusCode === 403 || statusCode === 401)
		{
			if (opts['auth_navigate']===false) throw new CustomError(statusCode, "Unauthorized");
			handleAuthFailure({ text: data?.detail ?? "Unauthorized", type: "error" });
			return false;
		};
		if (opts['streaming']) return response;
		if (!response.ok)
		{
			throw new CustomError( response.status, JSON.stringify(data), data);
			return false;
		};
		if (isJson && data?.results_recalculated)
		{
			resetScrollsOnCalculate();
		};
		return data;
	})
        .catch(error =>
	{
		if (opts.disable) { delete opts.disable.dataset.loading; opts.disable.disabled=false;};
		// Extract status code from error message
		let statusCode = error.status;
		let messageContent = error.message;
		if (statusCode in silentStatuses || opts.supressGlobalError) {
			let cb = silentStatuses[statusCode];
			if (cb) {
				cb(error);
			}
		} else {
			showGlobalError(statusCode, messageContent, opts.showFn);
		}
		throw error; // re-throw the error if you want to handle it further up the call stack
        });

};

function download(fu, params = {}, opts = {})
{
	return request(fu, params, { ...opts, raw: true, })
	.then(async (response) =>
	{
		if (!(response instanceof Response)) throw new Error("Expected raw Response for download");

		const blob = await response.blob();

		let filename = "download";
		const disposition = response.headers.get("Content-Disposition");
		let isInline = false;

		if (disposition)
		{
			isInline = disposition.toLowerCase().startsWith("inline");
			const match = disposition.match(/filename="?([^"]+)"?/);
			if (match) filename = decodeURIComponent(match[1]);
		};

		if (opts.filename) filename = opts.filename;

		const url = window.URL.createObjectURL(blob);

		if (isInline || opts.target === "_blank")
		{
			const newWindow = opts.target === "_blank" ? window.open("_blank") : window;
			if (!newWindow) throw new Error("Popup blocked");

			if (response.headers.get("Content-Type")?.includes("text/html"))
			{
				const reader = new FileReader();
				reader.onload = function () {
					newWindow.document.open();
					newWindow.document.write(reader.result);
					newWindow.document.close();
					newWindow.history.replaceState({}, "", filename);
				};
				reader.readAsText(blob, "utf-8");
			}
			else
			{
				newWindow.location.href = url;
			};
		}
		else
		{
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		};

		window.URL.revokeObjectURL(url);
		return true;
	});
}

return { request,download };

} // useApi
